import React, { ChangeEvent, SetStateAction, useEffect, useState } from 'react'
import { PairInterface } from '../../../types/interfaces'
import { useDispatch } from 'react-redux'
import { addNotification } from '../../../../store/slices/notificationSlice'
import { v4 } from 'uuid'
import { NotificationTypes } from '../../../types/enums'

export const useFileUpload = (
  files: File[],
  setFiles: React.Dispatch<SetStateAction<File[]>>,
  pairs: PairInterface[],
  setPairs: React.Dispatch<SetStateAction<PairInterface[]>>,
  min_length: number,
  length: number,
  extensions: string[],
  maxVideoCount: number = 1,
  minVideoCount: number = 0,
  totalLength?: number,
) => {
  const [drag, setDrag] = useState(false)
  const dispatch = useDispatch()

  const [errors, setErrors] = useState({
    quantity: false,
    min_quantity: min_length === 0 ? false : true,
    type: false,
    videoCount: false,
    totalCount: false,
  })

  function getExtension(filename: string) {
    const ext = filename.split('.').pop()
    if (ext) return ext.toLowerCase()
    return ''
  }

  function getFileType(file: File): 'image' | 'video' | 'document' | 'poster' {
    if (file.type.startsWith('image/')) return 'image'
    if (file.type.startsWith('video/')) return 'video'
    return 'document'
  }

  async function generatePoster(videoFile: File): Promise<File | null> {
    return new Promise((resolve) => {
      const video = document.createElement('video')
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      video.src = URL.createObjectURL(videoFile)
      video.currentTime = 0.5
      video.muted = true

      video.onloadeddata = () => {
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        ctx?.drawImage(video, 0, 0, canvas.width, canvas.height)
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const posterFile = new File([blob], `poster_${Date.now()}.webp`, {
                type: 'image/webp',
              })
              resolve(posterFile)
            } else {
              resolve(null)
            }
          },
          'image/webp',
          0.8,
        )
      }

      video.onerror = () => {
        resolve(null)
      }
    })
  }

  const addFiles = async (allNewFiles: File[]) => {
    const validatedFiles: File[] = []
    const newPairs: PairInterface[] = []
    let videoCount = 0
    let imageCount = 0

    const existingVideoCount = files.filter((f) => f.type.startsWith('video/')).length
    const existingImageCount = files.filter(
      (f) => f.type.startsWith('image/') && !f.name.includes('poster_'),
    ).length

    for (const file of allNewFiles) {
      const ext = getExtension(file.name)
      if (!extensions.includes(ext)) {
        dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: `Неверный формат: ${file.name}`,
          }),
        )
        continue
      }

      const type = getFileType(file)

      if (type === 'video') {
        if (existingVideoCount + videoCount >= maxVideoCount) {
          dispatch(
            addNotification({
              id: v4(),
              type: NotificationTypes.ERROR,
              text: `Можно загрузить не более ${maxVideoCount} видео`,
            }),
          )
          continue
        }
        videoCount++
      }

      if (type === 'image') {
        if (existingImageCount + imageCount >= length) {
          dispatch(
            addNotification({
              id: v4(),
              type: NotificationTypes.ERROR,
              text: `Можно загрузить не более ${length} картинок`,
            }),
          )
          continue
        }
        imageCount++
      }

      validatedFiles.push(file)
      newPairs.push({ file, url: URL.createObjectURL(file), type })

      if (type === 'video') {
        const poster = await generatePoster(file)
        if (poster) {
          validatedFiles.push(poster)
        }
      }
    }

    const totalVideoCount = existingVideoCount + videoCount
    if (totalVideoCount < minVideoCount) {
      setErrors({ ...errors, videoCount: true })
      dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.ERROR,
          text: `Необходимо загрузить минимум ${minVideoCount} видео`,
        }),
      )
      return
    } else {
      setErrors({ ...errors, videoCount: false })
    }

    const totalImages = existingImageCount + imageCount
    if (totalImages > length) {
      setErrors({ ...errors, quantity: true })
      return
    }

    // Проверка общего лимита (исключаем постеры)
    const totalFiles =
      files.filter((f) => !f.name.includes('poster_')).length +
      validatedFiles.filter((f) => !f.name.includes('poster_')).length

    if (totalLength !== undefined && totalFiles > totalLength) {
      setErrors({ ...errors, totalCount: true })
      dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.ERROR,
          text: `Можно загрузить не более ${totalLength} файлов`,
        }),
      )
      return
    } else {
      setErrors({ ...errors, totalCount: false })
    }

    setFiles([...files, ...validatedFiles])
    console.log('📁 После setFiles:', [...files, ...validatedFiles].map(f => f.name))
    setPairs([...pairs, ...newPairs])
  }

  function dragStartHandler(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDrag(true)
  }

  function dragLeaveHandler(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDrag(false)
  }

  async function onDropHandler(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    const allNewFiles = e.dataTransfer ? [...e.dataTransfer.files] : []
    await addFiles(allNewFiles)
    setDrag(false)
  }

  async function selectFiles(e: ChangeEvent<HTMLInputElement>) {
    const allNewFiles = e.target.files ? [...e.target.files] : []
    await addFiles(allNewFiles)
  }

  const deletePreview = (pair: PairInterface) => {
    setFiles(files.filter((el) => el !== pair.file))
    setPairs(pairs.filter((el) => el !== pair))
  }

  useEffect(() => {
    files.length >= min_length && setErrors({ ...errors, min_quantity: false })
    files.length < min_length && setErrors({ ...errors, min_quantity: true })
  }, [files])

  return {
    drag,
    files,
    dragStartHandler,
    dragLeaveHandler,
    onDropHandler,
    pairs,
    selectFiles,
    errors,
    deletePreview,
  }
}
